<template>
  <div class="energy-review-summary">
    <div v-if="loading" class="text-gray-500">집계 중...</div>
    <template v-else>
      <div v-if="!field" class="flex flex-wrap gap-6">
        <div><span class="font-bold mr-2">총 등록건수:</span><span>{{ totalCount }}건</span></div>
        <div><span class="font-bold mr-2">총 평가설비 수:</span><span>{{ totalEquipmentCount }}대</span></div>
        <div v-for="fieldName in fieldOrder" :key="fieldName">
          <span class="font-bold mr-2">{{ fieldName }}:</span><span>{{ fieldCounts[fieldName] || 0 }}건</span>
        </div>
      </div>
      <div v-else class="flex flex-wrap gap-6">
        <div><span class="font-bold mr-2">{{ field }} 등록건수:</span><span>{{ totalCount }}건</span></div>
        <div><span class="font-bold mr-2">평가설비 수:</span><span>{{ totalEquipmentCount }}대</span></div>
        <div><span class="font-bold mr-2">승인됨:</span><span>{{ statusCounts.approved }}건</span></div>
        <div><span class="font-bold mr-2">진행 중:</span><span>{{ statusCounts.ongoing }}건</span></div>
      </div>
    </template>
  </div>
</template>

<script>
const FIELD_NAME_KEY = "body.stdInfo.field_name";
const EQUIPMENT_KEY = "body.에너지평가[%].설비명";

const STATUS_ID = {
  APPROVED: [4],
  ONGOING: [2, 3],
};

const HEAD_KEY_MAP = {
  사업장명: "body.stdInfo.field_name",
  소속2: "body.stdInfo.department",
  공정: "body.에너지검토분류.공정명",
  설비명: "body.에너지평가[%].설비명",
  사용에너지: "body.에너지평가[%].사용에너지",
  "계측 가능여부": "body.에너지평가[%].계측가능여부",
  중요에너지등록여부: "body.에너지평가[%].평가품목_중요에너지등록부분류",
  작성자: "body.stdInfo.writer_name",
};

export default {
  name: "Board901Summary",
  props: {
    boardId: { type: [Number, String], required: true },
    nav: { type: Object, default: () => ({}) },
    projectId: { type: [Number, String], default: null },
    refreshKey: { type: Number, default: 0 },
  },
  data() {
    return {
      loading: false,
      totalCount: 0,
      totalEquipmentCount: 0,
      fieldCounts: {},
      statusCounts: { approved: 0, ongoing: 0 },
      fieldOrder: ["본사", "인천공장", "당진공장", "포항공장", "중앙기술연구소"],
    };
  },
  computed: {
    field() {
      return this.$route.query.fd || "";
    },
    year() {
      if (this.nav && this.nav.value && this.nav.value.year) {
        return this.nav.value.year;
      }
      return new Date().getFullYear();
    },
    resolvedProjectId() {
      if (this.projectId) return this.projectId;
      return this.$route.params.proj_id;
    },
  },
  watch: {
    refreshKey() {
      this.loadSummary();
    },
  },
  methods: {
    buildConditions() {
      const conditions = [];
      const year = parseInt(this.year);
      conditions.push({ key: "created_at", operator: ">=", value: `${year}-01-01` });
      conditions.push({ key: "created_at", operator: "<", value: `${year + 1}-01-01` });

      if (this.field) {
        conditions.push({ key: FIELD_NAME_KEY, operator: "=", value: this.field });
      }

      // 검색조건(column keyword) 반영
      const navValue = (this.nav && this.nav.value) || {};
      if (navValue.column && navValue.column.keyword && navValue.column.head) {
        const headKey = HEAD_KEY_MAP[navValue.column.head];
        if (headKey) {
          conditions.push({ key: headKey, operator: "LIKE", value: `%${navValue.column.keyword}%` });
        }
      }

      return conditions;
    },

    getBody(item) {
      if (item && item.postContents && item.postContents.body) {
        return item.postContents.body;
      }
      if (item && item.post_contents && item.post_contents.body) {
        return item.post_contents.body;
      }
      return null;
    },

    getFieldName(item) {
      const body = this.getBody(item);
      if (body && body.stdInfo && body.stdInfo.field_name) {
        return body.stdInfo.field_name;
      }
      return null;
    },

    countEquipment(item) {
      const body = this.getBody(item);
      if (!body || !Array.isArray(body["에너지평가"])) return 0;
      let count = 0;
      for (const row of body["에너지평가"]) {
        if (row && row["설비명"] !== null && row["설비명"] !== undefined && row["설비명"] !== "") {
          count++;
        }
      }
      return count;
    },

    async loadSummary() {
      this.loading = true;
      try {
        const conditions = this.buildConditions();
        const response = await this.$store.dispatch("approval/LOAD_POST_ALL", {
          boardSlug: `board-${this.boardId}`,
          projectId: this.resolvedProjectId,
          page: 1,
          perPage: 9999,
          orderTarget: "created_at",
          orderDirection: "desc",
          conditions,
          statusIds: [2, 3, 4],
          metaKeys: [FIELD_NAME_KEY, EQUIPMENT_KEY],
          withMeta: true,
          onlyMeta: true,
          withoutRelation: true,
          isParent: false,
        });

        const data = (response && response.data) || [];

        let equipmentCount = 0;
        const fieldCounts = {};
        const statusCounts = { approved: 0, ongoing: 0 };

        for (const item of data) {
          equipmentCount += this.countEquipment(item);

          const fieldName = this.getFieldName(item);
          if (fieldName) {
            fieldCounts[fieldName] = (fieldCounts[fieldName] || 0) + 1;
          }

          if (STATUS_ID.APPROVED.indexOf(item.document_status_id) >= 0) {
            statusCounts.approved++;
          } else if (STATUS_ID.ONGOING.indexOf(item.document_status_id) >= 0) {
            statusCounts.ongoing++;
          }
        }

        this.totalCount = data.length;
        this.totalEquipmentCount = equipmentCount;
        this.fieldCounts = fieldCounts;
        this.statusCounts = statusCounts;
      } catch (e) {
        console.error("[901 Summary] loadSummary error:", e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.energy-review-summary {
  width: 100%;
  font-size: 16px;
  padding: 12px 0;
}
</style>
